/**
 * Upsell Ordering Configuration
 * 
 * Controls the display order of upsells in the checkout popup based on
 * the selected pillow quantity. Each upsell is identified by its product ID.
 * 
 * The order array determines:
 * 1. Which upsell appears first (shown immediately after deal search)
 * 2. Which appears second (revealed after first is accepted)
 * 3. Which appears third (revealed after second is accepted)
 * 
 * To change the order for any quantity, simply rearrange the array.
 */

export type UpsellProductId = 'pillowcase-set' | 'silk-eyemask' | 'extra-pillow';

export interface UpsellOrderConfig {
  /** Display order of upsell product IDs for this quantity */
  order: [UpsellProductId, UpsellProductId, UpsellProductId];
  /** Default pillowcase quantity pre-fill for this quantity tier */
  defaultPillowcaseQuantity: number;
}

/**
 * Upsell order configuration keyed by pillow quantity.
 * 
 * Rationale:
 * - 1-pillow: Extra Pillow first (11% intake rate — highest for single buyers),
 *   then Pillowcase Set (qty 1), then Silk Eyemask
 * - 2-pillow: Pillowcase Set first (natural pairing), then Silk Eyemask, then Extra Pillow
 * - 4-pillow: Pillowcase Set first (qty 2 pre-fill), then Silk Eyemask, then Extra Pillow
 */
export const UPSELL_ORDER_BY_QUANTITY: Record<number, UpsellOrderConfig> = {
  1: {
    order: ['pillowcase-set', 'extra-pillow', 'silk-eyemask'],
    defaultPillowcaseQuantity: 1,
  },
  2: {
    order: ['pillowcase-set', 'silk-eyemask', 'extra-pillow'],
    defaultPillowcaseQuantity: 1,
  },
  4: {
    order: ['pillowcase-set', 'silk-eyemask', 'extra-pillow'],
    defaultPillowcaseQuantity: 2,
  },
};

/**
 * Get the upsell display order for a given quantity.
 * Falls back to the default (2-pillow) order for unknown quantities.
 */
export function getUpsellOrder(quantity: number): UpsellOrderConfig {
  return UPSELL_ORDER_BY_QUANTITY[quantity] ?? UPSELL_ORDER_BY_QUANTITY[2];
}
